import { Dialog, DialogContent, Divider, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  Macro,
  MacrosFormFields,
  MacrosFormModel,
  RecordFolder,
  RecordFolderArea,
  useChatMacros,
  useCurrentUserProfile,
  useFavoriteMacros,
  useGroupRules,
  useMacrosMethods,
  useRecordFolders,
  UserGroupRules,
  useSettingsMacros,
} from 'src/graphql';
import {
  useCreateRecordFolder,
  useDeleteRecordFolder,
  useUpdateRecordFolder,
} from 'src/graphql/entities/record-folder';
import { useMacroFolderAssignments } from 'src/graphql/entities/record-folder-assignment';
import { MacrosForm } from 'src/pages/Settings/macros/macros-form';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useConfirmationModal, useNotifications } from 'src/utils/hooks';
import {
  MacroListContent,
  MacrosModalEditorHeader,
  MacrosModalListHeader,
  MacrosModalSearch,
} from './components';
import { useMacroGrouping, useMacroListNavigation } from './hooks';

type EditorState =
  | {
      mode: 'list';
    }
  | {
      mode: 'edit';
      macroId?: string;
    };

export const MacrosModal = () => {
  const { isOpened, modalProps } = useModalState(Modals.MacrosModal);
  const { closeModal } = useModalMethods(Modals.MacrosModal);
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const { confirm } = useConfirmationModal();
  const { checkValidity } = useGroupRules();
  const { user } = useCurrentUserProfile();

  const [search, setSearch] = useState('');
  const [isManageMode, setIsManageMode] = useState(false);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());
  const [collapsedSearchFolderIds, setCollapsedSearchFolderIds] = useState<Set<string>>(
    new Set(),
  );
  const [editorState, setEditorState] = useState<EditorState>({ mode: 'list' });
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [deletingMacroId, setDeletingMacroId] = useState<string | null>(null);
  const [deletingFolderId, setDeletingFolderId] = useState<string | null>(null);
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');

  const conversation = modalProps?.conversation;
  const onMacroSelected = modalProps?.onMacroSelected;

  const canManageMacros = checkValidity([UserGroupRules.AccessToManageMacros]);
  const isPlatformUser = user?.partnerId === PLATFORM_PARTNER_ID;
  const canManagePlatformMacros = checkValidity([
    UserGroupRules.Admin,
    UserGroupRules.SuperAdmin,
  ]);
  const canManagePlatformLevelMacros = isPlatformUser && canManagePlatformMacros;
  const contextPartnerId = conversation?.partnerId ?? null;
  const isPlatformContext =
    isNullish(contextPartnerId) || contextPartnerId === PLATFORM_PARTNER_ID;
  const canManageInContext = isPlatformContext
    ? canManageMacros && canManagePlatformLevelMacros
    : canManageMacros;
  const resolvedPartnerId = isPlatformContext ? PLATFORM_PARTNER_ID : contextPartnerId;
  const canEditPlatformMacros = canManageMacros && canManagePlatformLevelMacros;
  const currentUserId = user?.id;

  const { interpolateMacroTemplate } = useMacrosMethods();

  const { chatMacros, loading: chatMacrosLoading } = useChatMacros({
    conversationPartnerId: conversation?.partnerId,
    skip: !isOpened || !conversation,
  });

  const {
    macros: settingsMacros,
    createMacro,
    updateMacro,
    deleteMacro,
    createMacroLoading,
    updateMacroLoading,
    createMacroToAssigneeGroupLoading,
    deleteMacroToAssigneeGroupLoading,
    loading: settingsMacrosLoading,
  } = useSettingsMacros({
    skip: !isOpened || !isManageMode || !canManageInContext,
    partnerId: contextPartnerId,
  });

  const assignmentPartnerId = contextPartnerId;
  const assignmentPartnerIds = useMemo(() => {
    const ids = [PLATFORM_PARTNER_ID, assignmentPartnerId].filter(
      (partnerId): partnerId is number => !isNullish(partnerId),
    );
    return Array.from(new Set(ids));
  }, [assignmentPartnerId]);

  const {
    recordFolderAssignments,
    setMacroFolderAssignment,
    isLoading: assignmentLoading,
  } = useMacroFolderAssignments({ partnerIds: assignmentPartnerIds });

  const recordFoldersPartnerId = contextPartnerId;

  const { recordFolders, isLoading: foldersLoading } = useRecordFolders({
    area: RecordFolderArea.Actions,
    partnerId: recordFoldersPartnerId,
    recordFolderAssignments,
  });

  const macroFolderAssignments = useMemo(
    () => recordFolderAssignments.filter((assignment) => assignment.entity === 'Macro'),
    [recordFolderAssignments],
  );

  const { createRecordFolder, isLoading: createFolderLoading } = useCreateRecordFolder();
  const { deleteRecordFolder, isLoading: deleteFolderLoading } = useDeleteRecordFolder();
  const { updateRecordFolder, isLoading: updateFolderLoading } = useUpdateRecordFolder();

  const macros = isManageMode ? settingsMacros : chatMacros;

  const visibleMacroIds = useMemo(() => new Set(macros.map((macro) => macro.id)), [macros]);

  const { favoriteMacroIds, toggleFavoriteMacro } = useFavoriteMacros({
    macros,
  });

  const favoriteMacroIdSet = useMemo(() => new Set(favoriteMacroIds), [favoriteMacroIds]);

  const isLoading =
    (isManageMode ? settingsMacrosLoading : chatMacrosLoading) ||
    foldersLoading ||
    assignmentLoading;

  const searchValue = search.trim().toLowerCase();
  const isSearching = Boolean(searchValue);

  const {
    groupedFolders,
    filteredEntries,
    effectiveExpandedFolders,
    visibleListEntries,
    visibleEntryIndexById,
  } = useMacroGrouping({
    macros,
    favoriteMacroIds,
    recordFolders,
    recordFolderAssignments: macroFolderAssignments,
    partnerIds: assignmentPartnerIds,
    expandedFolderIds,
    collapsedSearchFolderIds,
    isSearching,
    searchValue,
    isManageMode,
  });

  const toggleFolder = useCallback(
    (folderId: string) => {
      if (isSearching) {
        setCollapsedSearchFolderIds((prev) => {
          const next = new Set(prev);
          if (next.has(folderId)) {
            next.delete(folderId);
          } else {
            next.add(folderId);
          }
          return next;
        });
        return;
      }

      setExpandedFolderIds((prev) => {
        const next = new Set(prev);
        if (next.has(folderId)) {
          next.delete(folderId);
        } else {
          next.add(folderId);
        }
        return next;
      });
    },
    [isSearching],
  );

  const handleClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleMacroSelect = useCallback(
    async (macro: Macro) => {
      if (!conversation || !onMacroSelected) {
        return;
      }

      if (isManageMode) {
        const isPlatformMacro =
          isNullish(macro.partnerId) || macro.partnerId === PLATFORM_PARTNER_ID;
        const isOwner = Boolean(currentUserId && macro.createdBy === currentUserId);
        const canEdit = canEditPlatformMacros || !isPlatformMacro || isOwner;
        if (!canEdit) {
          showErrorAlert('Platform macros can only be edited by platform admins.');
          return;
        }

        setEditorState({ mode: 'edit', macroId: macro.id });
        return;
      }

      const interpolatedMessage = await interpolateMacroTemplate({
        text: macro.template,
        conversationId: conversation.id,
      });
      onMacroSelected(macro, interpolatedMessage);
      closeModal();
    },
    [
      closeModal,
      conversation,
      currentUserId,
      interpolateMacroTemplate,
      isManageMode,
      canEditPlatformMacros,
      onMacroSelected,
      showErrorAlert,
    ],
  );

  const {
    highlightedIndex,
    listItemRefs,
    resetHighlight,
    handleSearchKeyDown,
    handleListKeyDown,
    resetNavigation,
  } = useMacroListNavigation({
    visibleEntries: visibleListEntries,
    onSelectMacro: handleMacroSelect,
    onToggleFolder: toggleFolder,
  });

  useEffect(() => {
    if (!isOpened) {
      setSearch('');
      setIsManageMode(false);
      setEditorState({ mode: 'list' });
      setExpandedFolderIds(new Set());
      setCollapsedSearchFolderIds(new Set());
      resetNavigation();
      setIsCreatingFolder(false);
      setNewFolderName('');
      setDeletingMacroId(null);
      setDeletingFolderId(null);
      setEditingFolderId(null);
      setEditingFolderName('');
    }
  }, [isOpened, resetNavigation]);

  useEffect(() => {
    if (!isSearching) {
      setCollapsedSearchFolderIds(new Set());
    }
  }, [isSearching]);

  const handleCreateMacro = useCallback(() => {
    setEditorState({ mode: 'edit' });
  }, []);

  const handleToggleManageMode = useCallback(() => {
    if (!canManageInContext) {
      return;
    }

    setIsManageMode((prev) => {
      const next = !prev;
      if (!next) {
        setIsCreatingFolder(false);
        setNewFolderName('');
        setEditingFolderId(null);
        setEditingFolderName('');
      }
      return next;
    });
    setEditorState({ mode: 'list' });
  }, [canManageInContext]);

  const handleDeleteMacro = useCallback(
    async (macro: Macro) => {
      if (deletingMacroId || !canManageInContext) {
        return;
      }

      const isPlatformMacro =
        isNullish(macro.partnerId) || macro.partnerId === PLATFORM_PARTNER_ID;
      const isOwner = Boolean(currentUserId && macro.createdBy === currentUserId);
      const canDelete = canEditPlatformMacros || !isPlatformMacro || isOwner;
      if (!canDelete) {
        showErrorAlert('Platform macros can only be deleted by platform admins.');
        return;
      }

      const shouldDelete = await confirm({
        body: 'Please confirm you want to delete this macro. This process cannot be undone.',
        variant: 'delete',
      });

      if (!shouldDelete) {
        return;
      }

      setDeletingMacroId(macro.id);
      try {
        await deleteMacro(macro.id);
        showSuccessAlert('Macro deleted.');
      } catch (error) {
        console.error(error);
        showErrorAlert('Failed to delete macro. Please try again.');
      } finally {
        setDeletingMacroId(null);
      }
    },
    [
      canManageInContext,
      canEditPlatformMacros,
      currentUserId,
      confirm,
      deleteMacro,
      deletingMacroId,
      showErrorAlert,
      showSuccessAlert,
    ],
  );

  const handleDeleteFolder = useCallback(
    async (folder: RecordFolder) => {
      if (deletingFolderId || !canManageInContext) {
        return;
      }

      const shouldDelete = await confirm({
        body: 'Delete this folder? Macros inside will move to Uncategorized.',
        variant: 'delete',
      });

      if (!shouldDelete) {
        return;
      }

      setDeletingFolderId(folder.id);
      try {
        await deleteRecordFolder(folder);
        showSuccessAlert('Folder deleted.');
      } catch (error) {
        console.error(error);
        showErrorAlert('Failed to delete folder. Please try again.');
      } finally {
        setDeletingFolderId(null);
      }
    },
    [
      canManageInContext,
      confirm,
      deleteRecordFolder,
      deletingFolderId,
      showErrorAlert,
      showSuccessAlert,
    ],
  );

  const handleStartRenameFolder = useCallback((folder: RecordFolder) => {
    setEditingFolderId(folder.id);
    setEditingFolderName(folder.name);
  }, []);

  const handleCancelRenameFolder = useCallback(() => {
    setEditingFolderId(null);
    setEditingFolderName('');
  }, []);

  const handleRenameFolder = useCallback(
    (folder: RecordFolder) => {
      const trimmedName = editingFolderName.trim();
      if (!trimmedName || trimmedName === folder.name) {
        handleCancelRenameFolder();
        return;
      }

      updateRecordFolder(folder, { name: trimmedName }, () => {
        setEditingFolderId(null);
        setEditingFolderName('');
        showSuccessAlert('Folder renamed.');
      });
    },
    [editingFolderName, handleCancelRenameFolder, showSuccessAlert, updateRecordFolder],
  );

  const handleCreateFolder = useCallback(async () => {
    const trimmedName = newFolderName.trim();
    if (!trimmedName) {
      return;
    }

    const createdFolder = await createRecordFolder({
      name: trimmedName,
      isPrivate: false,
      icon: 'folder',
      area: RecordFolderArea.Actions,
      partnerId: resolvedPartnerId,
    });

    setIsCreatingFolder(false);
    setNewFolderName('');

    if (createdFolder) {
      setExpandedFolderIds((prev) => new Set(prev).add(createdFolder.id));
    }
  }, [createRecordFolder, newFolderName, resolvedPartnerId]);

  const handleEditorCancel = useCallback(() => {
    setEditorState({ mode: 'list' });
  }, []);

  const handleSaveMacro = useCallback(
    async (values: MacrosFormModel, macroId?: string) => {
      if (!canManageInContext) {
        return;
      }

      const existingMacro = macroId
        ? (settingsMacros.find((macro) => macro.id === macroId) ?? null)
        : null;
      const targetPartnerId = isNullish(existingMacro?.partnerId)
        ? resolvedPartnerId
        : existingMacro?.partnerId;
      const isPlatformMacro = targetPartnerId === PLATFORM_PARTNER_ID;
      const isOwner = Boolean(currentUserId && existingMacro?.createdBy === currentUserId);

      if (isPlatformMacro && !canEditPlatformMacros && !isOwner) {
        showErrorAlert('Platform macros can only be edited by platform admins.');
        return;
      }

      const folderId = values[MacrosFormFields.RecordFolderId]?.id || null;
      const title = values[MacrosFormFields.Name];

      try {
        if (macroId) {
          await updateMacro(macroId, values, targetPartnerId ?? PLATFORM_PARTNER_ID);
          await setMacroFolderAssignment({
            macroId,
            folderId: folderId || null,
            title,
            partnerId: targetPartnerId ?? PLATFORM_PARTNER_ID,
          });
        } else {
          const createdMacroId = await createMacro(values);
          if (createdMacroId) {
            await setMacroFolderAssignment({
              macroId: createdMacroId,
              folderId: folderId || null,
              title,
              partnerId: resolvedPartnerId,
            });
          } else {
            return;
          }
        }

        setEditorState({ mode: 'list' });
      } catch (error) {
        console.error(error);
        showErrorAlert('Failed to save macro. Please try again.');
      }
    },
    [
      canManageInContext,
      canEditPlatformMacros,
      currentUserId,
      createMacro,
      resolvedPartnerId,
      setMacroFolderAssignment,
      showErrorAlert,
      settingsMacros,
      updateMacro,
    ],
  );

  const isMacroFavoritable = useCallback(
    (macro: Macro) => visibleMacroIds.has(macro.id),
    [visibleMacroIds],
  );

  if (!isOpened || !conversation) {
    return null;
  }

  const isEditing = editorState.mode === 'edit';
  const selectedMacroId = editorState.mode === 'edit' ? editorState.macroId : undefined;
  const activeEntryId = visibleListEntries[highlightedIndex]?.id ?? null;
  const savingMacro =
    createMacroLoading ||
    updateMacroLoading ||
    createMacroToAssigneeGroupLoading ||
    deleteMacroToAssigneeGroupLoading ||
    assignmentLoading;

  return (
    <Dialog
      open={isOpened}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { height: '60vh' } }}
      sx={{ '& .MuiDialog-container': { alignItems: 'center' } }}
    >
      <DialogContent sx={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
        <Stack paddingX={3} paddingTop={3} paddingBottom={2}>
          {isEditing ? (
            <MacrosModalEditorHeader
              title={selectedMacroId ? 'Edit Macro' : 'New Macro'}
              onBack={handleEditorCancel}
            />
          ) : (
            <MacrosModalListHeader
              isManageMode={isManageMode}
              canManageMacros={canManageInContext}
              onCreateMacro={handleCreateMacro}
              onCreateFolder={() => setIsCreatingFolder(true)}
              onToggleManageMode={handleToggleManageMode}
            />
          )}
        </Stack>
        <Divider />
        {!isEditing ? (
          <Stack paddingX={3} paddingY={2}>
            <MacrosModalSearch
              value={search}
              onChange={(value) => {
                setSearch(value);
                resetHighlight();
              }}
              onKeyDown={handleSearchKeyDown}
            />
          </Stack>
        ) : null}
        {!isEditing ? <Divider /> : null}
        {isEditing ? (
          <Stack flex={1} overflow="auto" padding={3}>
            <MacrosForm
              macroId={selectedMacroId}
              onSaveMacro={handleSaveMacro}
              loading={savingMacro}
              onCancel={handleEditorCancel}
              contextPartnerId={contextPartnerId}
            />
          </Stack>
        ) : (
          <MacroListContent
            isCreatingFolder={isCreatingFolder}
            newFolderName={newFolderName}
            onNewFolderNameChange={setNewFolderName}
            onCreateFolder={handleCreateFolder}
            onCancelCreateFolder={() => {
              setIsCreatingFolder(false);
              setNewFolderName('');
            }}
            createFolderLoading={createFolderLoading}
            isLoading={isLoading}
            isSearching={isSearching}
            hasResults={filteredEntries.length > 0}
            groupedFolders={groupedFolders}
            expandedFolderIds={effectiveExpandedFolders}
            onToggleFolder={toggleFolder}
            isManageMode={isManageMode}
            canManageMacros={canManageInContext}
            canEditPlatformMacros={canEditPlatformMacros}
            currentUserId={currentUserId}
            deletingFolderId={deletingFolderId}
            deleteFolderLoading={deleteFolderLoading}
            onDeleteFolder={handleDeleteFolder}
            editingFolderId={editingFolderId}
            editingFolderName={editingFolderName}
            updateFolderLoading={updateFolderLoading}
            onStartRenameFolder={handleStartRenameFolder}
            onCancelRenameFolder={handleCancelRenameFolder}
            onRenameFolder={handleRenameFolder}
            onEditFolderNameChange={setEditingFolderName}
            deletingMacroId={deletingMacroId}
            favoriteMacroIds={favoriteMacroIdSet}
            isMacroFavoritable={isMacroFavoritable}
            onToggleFavoriteMacro={(macro) => toggleFavoriteMacro(macro.id)}
            onSelectMacro={handleMacroSelect}
            onDeleteMacro={handleDeleteMacro}
            onListKeyDown={handleListKeyDown}
            highlightedIndex={highlightedIndex}
            activeEntryId={activeEntryId}
            visibleEntryIndexById={visibleEntryIndexById}
            listItemRefs={listItemRefs}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
